import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import {
  HStack,
  useToast,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Checkbox,
  Switch,
} from '@chakra-ui/react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import context from '../context';
import Input from '../../Form/Input';
import Datepicker from '../../Form/Datepicker';
import {
  CREATE_GROUP_ELEMENT,
  REMOVE_GROUP_ELEMENT,
  UPDATE_GROUP_ELEMENT,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';

interface FormData {
  name: string;
  reference_id: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const formData = yup.object().shape({
  // name: yup.string().required('Nome é obrigatório'),
  // reference_id: yup.string().required('Sku é obrigatório'),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  schedule_start: yup.date().when('schedule', {
    is: true,
    then: yup.date().required('La fecha de inicio es obligatoria'),
  }),
  schedule_end: yup
    .date()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(
            schedule_start,
            'La fecha final debe ser posterior a la fecha de inicio',
          )
          .required('La fecha final es obligatoria');
      }
      return yup.date().required('La fecha final es obligatoria');
    }),
});

interface Props {
  onClose: () => void;
  onCreated?: () => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
  isOpen: boolean;
  product?: any;
}

const ProductForm: React.FC<Props> = ({
  onClose,
  isOpen,
  onCreated,
  product,
  onUpdated,
  onDeleted,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { pageGroup, element } = useContext(context);

  const { control, register, handleSubmit, formState, reset, setValue } =
    useForm({
      resolver: yupResolver(formData),
    });

  const [createGroupElement] = useMutation(CREATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [updateGroupElement] = useMutation(UPDATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [removeGroupElement, { loading: removeLoading, error: removeError }] =
    useMutation(REMOVE_GROUP_ELEMENT, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const { errors } = formState;

  const [schedule, setSchedule] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!product) return;
    setValue('reference_id', product?.reference_id);
    setValue('name', product?.name);
    setValue('countdown', product?.countdown);

    if (product?.schedule_end) {
      setSchedule(true);
      setValue('schedule', true);
      setValue('schedule_start', new Date(product?.schedule_start));
      setValue('schedule_end', new Date(product?.schedule_end));
    }
  }, [product, setValue]);

  useEffect(() => {
    if (removeError) {
      toast({
        title: 'Opss!!',
        description: removeError?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    }
  }, [removeError, toast, t]);

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(t('confirmBannerDeletion'));
      if (confirmed) {
        await removeGroupElement({
          variables: {
            id: product?.id,
          },
        });
        if (onDeleted) onDeleted();
        reset();
        setSchedule(false);
        toast({
          title: t('success'),
          description: t('bannerDeletedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // error
    }
  };

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      setLoading(true);
      const { schedule: scheduleData, ...rest } = data;

      if (product) {
        const variables: any = {
          updateGroupElementInput: {
            ...rest,
            id: product?.id,
            reference_id: data.reference_id,
            schedule_end: scheduleData ? rest.schedule_end : null,
            countdown: scheduleData ? rest.countdown : false,
          },
        };
        await updateGroupElement({
          variables,
        });
        if (onUpdated) onUpdated();
        reset();
        setSchedule(false);
        toast({
          title: t('success'),
          description: t('bannerEditedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        await createGroupElement({
          variables: {
            createGroupElementInput: {
              ...rest,
              page_group_id: pageGroup?.id,
              element_id: element?.id,
            },
          },
        });
        if (onCreated) onCreated();
        reset();
        setSchedule(false);
        toast({
          title: t('success'),
          description: t('bannerCreatedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description:
          err?.response?.data?.message || err?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      onClose={() => {
        reset();
        setSchedule(false);
        onClose();
      }}
      isOpen={isOpen}
      size="lg"
    >
      <DrawerOverlay zIndex={1_400} />
      <DrawerContent>
        <DrawerHeader>{t('newProduct')}</DrawerHeader>
        <DrawerBody>
          <VStack as="form" spacing="8">
            <Input
              placeholder={t('nameField')}
              error={errors.name as FieldError | undefined}
              {...register('name')}
            />
            <Input
              placeholder="SKU"
              error={errors.reference_id as FieldError | undefined}
              {...register('reference_id')}
            />
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="schedule" mb="0">
                {t('schedule')}
              </FormLabel>
              <Switch
                id="schedule"
                size="lg"
                colorScheme="brand"
                isChecked={schedule}
                {...register('schedule')}
                onChange={() => setSchedule(state => !state)}
              />
            </FormControl>
            {schedule && (
              <>
                <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
                  <Datepicker
                    isRequired
                    control={control}
                    name="schedule_start"
                    label={t('scheduleStart')}
                    showTimeSelect
                    error={errors.schedule_start as FieldError | undefined}
                  />
                  <Datepicker
                    isRequired
                    control={control}
                    name="schedule_end"
                    label={t('scheduleEnd')}
                    showTimeSelect
                    error={errors.schedule_en as FieldError | undefined}
                  />
                </SimpleGrid>
                <Checkbox
                  alignSelf="flex-start"
                  colorScheme="brand"
                  {...register('countdown')}
                >
                  {t('countdown')}
                </Checkbox>
              </>
            )}
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <HStack spacing={4}>
            {!!product && (
              <Button
                isLoading={removeLoading}
                colorScheme="red"
                onClick={handleDelete}
              >
                {t('remove')}
              </Button>
            )}
            <Button
              isLoading={loading}
              colorScheme="brand"
              onClick={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
            >
              {t('save')}
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ProductForm;
